import { Link } from "@tanstack/react-router";
import logo from "@/assets/p2pro-logo.webp";
import { memo, useState } from "react";
import { cn } from "@/lib/utils";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface SubmenuItem {
  label: string;
  href: string;
}

interface NavLink {
  label: string;
  href: string;
  submenu?: SubmenuItem[];
}

const NAV_LINKS: NavLink[] = [
  { label: "Início", href: "/" },
  { 
    label: "Painel IPTV", 
    href: "/painel-iptv",
    submenu: [
      { label: "Painel IPTV Pós Pago", href: "/painel-iptv/pos-pago" },
      { label: "Melhor Painel IPTV", href: "/painel-iptv/melhor" },
      { label: "Franquia IPTV", href: "/painel-iptv/franquia" },
      { label: "Painel IPTV para Revendedores", href: "/painel-iptv/revendedores" },
      { label: "Como Funciona um Painel IPTV", href: "/painel-iptv/como-funciona" },
    ]
  },
  { 
    label: "Curso IPTV", 
    href: "/curso-iptv",
    submenu: [
      { label: "Curso IPTV (iniciantes)", href: "/curso-iptv" },
    ]
  },
  { 
    label: "Revenda IPTV", 
    href: "/revenda-iptv",
    submenu: [
      { label: "Como Ser Revendedor IPTV", href: "/revenda-iptv/como-ser" },
      { label: "Revenda IPTV Pós Pago", href: "/revenda-iptv/pos-pago" },
      { label: "Plano Revendedor IPTV", href: "/revenda-iptv/plano" },
      { label: "Quanto Ganha um Revendedor IPTV", href: "/revenda-iptv/quanto-ganha" },
      { label: "Revenda IPTV para Iniciantes", href: "/revenda-iptv/iniciantes" },
    ]
  },
  { 
    label: "Aplicativos IPTV", 
    href: "/aplicativos-iptv",
    submenu: [
      { label: "One X", href: "/aplicativos-iptv/one-x" },
      { label: "Fun Play", href: "/aplicativos-iptv/funplay" },
      { label: "Lazer Play", href: "/aplicativos-iptv/lazer-play" },
      { label: "Blessed", href: "/aplicativos-iptv/blessed" },
      { label: "VuPlayer", href: "/aplicativos-iptv/vuplayer" },
                    { label: "PlaySim", href: "/aplicativos-iptv/playsim" },
                    { label: "Assist Plus", href: "/aplicativos-iptv/assist-plus" },
                    { label: "IBO Player", href: "/aplicativos-iptv/ibo-player" },
    ]
  },
  { 
    label: "Tutoriais IPTV", 
    href: "/tutoriais-iptv",
    submenu: [
      { label: "IPTV na TV Samsung", href: "/tutoriais-iptv/samsung" },
      { label: "IPTV na TV LG", href: "/tutoriais-iptv/lg" },
      { label: "IPTV na TV Box", href: "/tutoriais-iptv/tv-box" },
      { label: "IPTV no Fire Stick", href: "/tutoriais-iptv/fire-stick" },
      { label: "IPTV no Android", href: "/tutoriais-iptv/android" },
      { label: "IPTV no iPhone", href: "/tutoriais-iptv/iphone" },
      { label: "Configuração de Aplicativos IPTV", href: "/tutoriais-iptv/configuracao" },
    ]
  },
  { label: "Blog", href: "/blog" },
  { label: "Contato", href: "/contato" },
];

export const Nav = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const whatsappUrl = `https://api.whatsapp.com/send?phone=5583986147064&text=${encodeURIComponent("Olá! Vim do site p2propaineliptv.com.br, gostaria de mais informações.")}`;

  return (
    <header className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300">
      <div className="absolute inset-0 bg-background/60 backdrop-blur-md border-b border-white/5" />
      
      <nav className="relative max-w-7xl mx-auto px-6 h-20 md:h-24 flex items-center justify-between">
        <Link 
          to="/" 
          className="flex items-center gap-4 group"
          aria-label="P2 PRO Home"
        >
          <img 
            src={logo} 
            alt="P2 PRO Logo" 
            className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105" 
            width={48}
            height={48}
            loading="eager"
            fetchPriority="high"
          />
          <span className="font-black text-xl tracking-tighter uppercase">
            P2<span className="text-electric ml-0.5">PRO</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-6">
          {NAV_LINKS.map(link => (
            <div 
              key={link.href} 
              className="relative group/menu"
            >
              {link.submenu ? (
                <div
                  className={cn(
                    "text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5 py-8 cursor-pointer",
                    "hover:text-electric transition-all duration-300"
                  )}
                >
                  {link.label}
                  <ChevronDown className="w-3 h-3 opacity-50 group-hover/menu:rotate-180 transition-transform" />
                </div>
              ) : (
                <Link
                  to={link.href as any}
                  className={cn(
                    "text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1.5 py-8 cursor-pointer",
                    "hover:text-electric transition-all duration-300"
                  )}
                >
                  {link.label}
                </Link>
              )}

              {link.submenu && (
                <div className="absolute top-full left-0 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/menu:opacity-100 group-hover/menu:translate-y-0 group-hover/menu:pointer-events-auto transition-all duration-300">
                  <div className="bg-[#12141a] border border-white/5 rounded-2xl p-4 min-w-[240px] shadow-2xl backdrop-blur-xl">
                    <div className="flex flex-col gap-1">
                      {link.submenu.map(sub => (
                        <Link
                          key={sub.href}
                          to={sub.href as any}
                          className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground p-3 rounded-lg hover:bg-white/5 hover:text-electric transition-all"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <a 
            href={whatsappUrl} 
            target="_blank"
            rel="noopener noreferrer"

            className={cn(
              "hidden sm:flex bg-electric text-black px-8 py-3.5 rounded-full",
              "font-black text-[11px] uppercase tracking-[0.2em]",
              "hover:shadow-glow hover:scale-105 transition-all active:scale-95"
            )}
          >
            Quero Revender
          </a>

          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="xl:hidden w-12 h-12 flex items-center justify-center text-foreground p-2"
            aria-label={isOpen ? "Fechar Menu" : "Abrir Menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="xl:hidden absolute top-20 left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-white/5 p-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
          >
            {NAV_LINKS.map(link => (
              <div key={link.href} className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  {link.submenu ? (
                    <div
                      onClick={() => setActiveSubmenu(activeSubmenu === link.label ? null : link.label)}
                      className="text-xs font-black uppercase tracking-widest text-foreground hover:text-electric transition-colors cursor-pointer flex-1"
                    >
                      {link.label}
                    </div>
                  ) : (
                    <Link
                      to={link.href as any}
                      onClick={() => setIsOpen(false)}
                      className="text-xs font-black uppercase tracking-widest text-foreground hover:text-electric transition-colors cursor-pointer flex-1"
                    >
                      {link.label}
                    </Link>
                  )}
                  {link.submenu && (
                    <button 
                      onClick={() => setActiveSubmenu(activeSubmenu === link.label ? null : link.label)}
                      className="p-2"
                    >
                      <ChevronDown className={cn("w-4 h-4 transition-transform", activeSubmenu === link.label && "rotate-180")} />
                    </button>
                  )}
                </div>
                
                {link.submenu && activeSubmenu === link.label && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    className="flex flex-col gap-3 pl-4 border-l border-white/5 ml-1 my-2"
                  >
                    {link.submenu.map(sub => (
                      <Link
                        key={sub.href}
                        to={sub.href as any}
                        onClick={() => setIsOpen(false)}
                        className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-electric transition-colors"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </div>
            ))}
            <a 
              href={whatsappUrl} 
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="bg-electric text-white px-8 py-4 rounded-full font-black text-center text-xs uppercase tracking-widest mt-4"
            >
              Quero Revender
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
});

Nav.displayName = "Nav";